import type { SiroccoEvent } from '@sirocco/shared';
import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { events } from '../../api/endpoints';
import { EventCard } from '../../components/EventCard';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { EmptyState, Tabs } from '../../components/ui/Bits';
import { useAsync } from '../../hooks/useAsync';
import { useSwipeTabs } from '../../hooks/useSwipeTabs';
import { useTabMemory } from '../../hooks/useTabMemory';
import { isThisWeek } from '../../utils/format';
import { minutesDe } from '../../utils/spotTime';
import { t } from '../../i18n';

type Tab = 'upcoming' | 'organized';

/** Les deux onglets, hors du rendu : le geste de balayage les lit aussi. */
const ONGLETS: { value: Tab; label: string }[] = [
  { value: 'upcoming', label: t.sorties.agenda.onglets.aVenir },
  { value: 'organized', label: t.sorties.agenda.onglets.jOrganise },
];

/**
 * Mon agenda : ce qui m'attend, decoupe en "cette semaine" et "plus
 * tard" pour qu'un coup d'oeil suffise.
 *
 * L'agenda ne regarde que devant : les evenements deja vecus se
 * retrouvent dans "Mes evenements", depuis le profil. Le second onglet
 * separe ce que j'organise de ce ou je suis invite — les deux ne
 * demandent pas le meme travail.
 */
export function AgendaScreen() {
  const navigate = useNavigate();
  const [tab, setTab] = useTabMemory<Tab>('mon-agenda', 'upcoming', ONGLETS);
  /* Le balayage double le clic sur le titre : c'est le geste qu'on essaie
     devant deux onglets cote a cote. */
  const zoneRef = useRef<HTMLDivElement>(null);
  useSwipeTabs(zoneRef, tab, ONGLETS, setTab);
  const { data, loading, error, reload } = useAsync(() => events.mine(), [], 'agenda');

  /*
    « A venir », c'est tout ce qui m'attend, ce que j'organise compris.

    La couche donnee separe les deux — elle sert aussi l'onglet
    « J'organise », ou ma propre sortie ne doit pas se compter deux fois.
    Mais un agenda qui oublie mes propres rendez-vous ne dit pas ce que
    je fais jeudi : j'y suis, et c'est meme moi qui recois.
  */
  const aVenir = [...(data?.joined ?? []), ...(data?.organized ?? [])].sort((a, b) =>
    a.date === b.date ? minutesDe(a.startTime) - minutesDe(b.startTime) : a.date < b.date ? -1 : 1,
  );
  const list = tab === 'upcoming' ? aVenir : (data?.organized ?? []);
  const thisWeek = list.filter((event) => isThisWeek(event.date));
  const later = list.filter((event) => !isThisWeek(event.date));

  return (
    <SubScreen title={t.sorties.agenda.titre}>
      <div className="ev-body">
        <div ref={zoneRef}>
          <Tabs<Tab> value={tab} onChange={setTab} options={ONGLETS} />
        </div>

        {loading && <ScreenLoader />}
        {error && <ScreenError message={error} onRetry={reload} />}

        {data && (
          <>
            {thisWeek.length > 0 && (
              <AgendaSection
                title={t.sorties.agenda.semaine}
                list={thisWeek}
              />
            )}
            {later.length > 0 && (
              <AgendaSection title={t.sorties.agenda.plusTard} list={later} />
            )}

            {list.length === 0 && (
              <EmptyState
                title={t.sorties.agenda.rien}
                hint={
                  tab === 'upcoming'
                    ? t.sorties.agenda.rienAide
                    : t.sorties.agenda.rienOrganise
                }
                action={
                  tab === 'upcoming'
                    ? { label: t.sorties.agenda.voir, onClick: () => navigate('/app/events') }
                    : { label: t.sorties.agenda.proposer, onClick: () => navigate('/app/events/new') }
                }
              />
            )}

          </>
        )}
      </div>
    </SubScreen>
  );
}

/** Un bloc de l'agenda, precede de son intertitre. */
function AgendaSection({ title, list }: { title: string; list: SiroccoEvent[] }) {
  return (
    <section className="ev-agenda-section">
      <h2 className="ev-agenda-section__title">{title}</h2>
      <div className="ev-agenda-list">
        {list.map((event) => (
          <EventCard key={event.id} event={event} />
        ))}
      </div>
    </section>
  );
}

